<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">Gestão de Usuários Cognito</h1>
      <div class="header-actions">
        <button class="btn btn-primary" @click="exportToExcel">
          <span class="btn-icon">📊</span>
          Exportar Excel
        </button>
        <button class="btn btn-secondary" @click="showFilters = !showFilters">
          <span class="btn-icon">🔍</span>
          Filtros
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Carregando dados...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <p>Erro ao carregar dados: {{ error }}</p>
      <button class="btn btn-primary" @click="fetchUsers">Tentar Novamente</button>
    </div>

    <!-- Filtros -->
    <div v-else>
      <div v-if="showFilters" class="filters-section">
        <div class="filter-row">
          <div class="filter-group">
            <label>Buscar</label>
            <input 
              v-model="filters.search" 
              type="text" 
              placeholder="Digite para buscar..."
              class="filter-input"
            >
          </div>
          <div class="filter-group">
            <label>Origem</label>
            <select v-model="filters.tipo" class="filter-select">
              <option value="">Todos</option>
              <option value="APP">APP</option>
              <option value="WEB">WEB</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Status</label>
            <select v-model="filters.status" class="filter-select">
              <option value="">Todos</option>
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Plano</label>
            <select v-model="filters.plano" class="filter-select">
              <option value="">Todos</option>
              <option value="PLANO_PLUS_01">PLANO PLUS 01</option>
              <option value="PLANO_BASIC">PLANO BASIC</option>
              <option value="PLANO_ENTERPRISE">PLANO ENTERPRISE</option>
            </select>
          </div>
        </div>
      </div>

      <!-- DataGrid -->
      <div class="datagrid-container">
        <div class="table-responsive">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-for="column in columns" :key="column.key" :class="column.class">
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in paginatedData" :key="item.id" :class="{ 'inactive': !item.enabled }">
                <td class="id-column">{{ item.id }}</td>
                <td class="name-column">{{ item.name }}</td>
                <td class="cnpj-column">{{ formatCNPJ(item.cnpj) }}</td>
                <td class="email-column">{{ item.email }}</td>
                <td class="type-column">
                  <span class="badge" :class="getTipoClass(item.tipo)">{{ item.tipo }}</span>
                </td>
                <td class="payment-column">{{ item.phone_number }}</td>
                <td class="plan-column">
                  <span class="badge" :class="getPlanoClass(item.plano)">{{ formatPlano(item.plano) }}</span>
                </td>
                <td class="status-column">
                  <span class="status-badge" :class="item.status === 'Ativo' ? 'active' : 'inactive'">
                    {{ item.status }}
                  </span>
                </td>
                <td class="actions-column">
                  <div class="action-buttons">
                    <button class="btn-action view" @click="viewItem(item)" title="Visualizar">
                      👁️
                    </button>
                    <button class="btn-action edit" @click="editItem(item)" title="Editar">
                      ✏️
                    </button>
                    <button 
                      class="btn-action" 
                      :class="item.enabled ? 'block' : 'unblock'" 
                      @click="toggleStatus(item)"
                      :title="item.enabled ? 'Bloquear' : 'Desbloquear'"
                    >
                      {{ item.enabled ? '🔒' : '🔓' }}
                    </button>
                    <button class="btn-action delete" @click="deleteItem(item)" title="Excluir">
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ startIndex }}-{{ endIndex }} de {{ filteredData.length }} registros
          </div>
          <div class="pagination-controls">
            <button 
              class="pagination-btn" 
              :disabled="currentPage === 1"
              @click="currentPage--"
            >
              ‹
            </button>
            <span class="pagination-page">
              Página {{ currentPage }} de {{ totalPages }}
            </span>
            <button 
              class="pagination-btn" 
              :disabled="currentPage === totalPages"
              @click="currentPage++"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api.js';

export default {
  name: 'PageDefault',
  data() {
    return {
      loading: false,
      error: null,
      showFilters: false,
      currentPage: 1,
      itemsPerPage: 10,
      filters: {
        search: '',
        tipo: '',
        status: '',
        plano: ''
      },
      columns: [
        { key: 'id', label: 'ID', class: 'id-column' },
        { key: 'name', label: 'Nome', class: 'name-column' },
        { key: 'cnpj', label: 'CNPJ', class: 'cnpj-column' },
        { key: 'email', label: 'Email', class: 'email-column' },
        { key: 'tipo', label: 'Origem', class: 'type-column' },
        { key: 'phone_number', label: 'Telefone', class: 'payment-column' },
        { key: 'plano', label: 'Plano', class: 'plan-column' },
        { key: 'status', label: 'Status', class: 'status-column' },
        { key: 'actions', label: 'Ações', class: 'actions-column' }
      ],
      data: []
    }
  },
  computed: {
    filteredData() {
      return this.data.filter(item => {
        const matchesSearch = !this.filters.search || 
          Object.values(item).some(value => 
            String(value).toLowerCase().includes(this.filters.search.toLowerCase())
          );
        const matchesTipo = !this.filters.tipo || item.tipo === this.filters.tipo;
        const matchesStatus = !this.filters.status || item.status === this.filters.status;
        const matchesPlano = !this.filters.plano || item.plano === this.filters.plano;
        
        return matchesSearch && matchesTipo && matchesStatus && matchesPlano;
      });
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredData.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endIndex() {
      const end = this.currentPage * this.itemsPerPage;
      return end > this.filteredData.length ? this.filteredData.length : end;
    }
  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1;
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await apiClient.get('/cognito/users');
        
        if (response.data.success) {
          // Transformar os dados da API para o formato esperado pelo componente
          this.data = response.data.users.map((user, index) => ({
            id: index + 1,
            username: user.username,
            name: user.attributes?.name || 'N/A',
            cnpj: user.attributes?.['custom:cnpj'] || 'N/A',
            email: user.attributes?.email || 'N/A',
            tipo: user.attributes?.['custom:origem_cadast'] || 'N/A',
            phone_number: user.attributes?.phone_number || 'N/A',
            plano: user.attributes?.['custom:plano'] || 'N/A',
            enabled: user.enabled,
            status: user.enabled ? 'Ativo' : 'Inativo',
            userStatus: user.userStatus,
            userCreateDate: user.userCreateDate,
            userLastModifiedDate: user.userLastModifiedDate,
            attributes: user.attributes
          }));
        } else {
          throw new Error('Resposta da API não indica sucesso');
        }
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        this.error = error.message || 'Erro ao carregar dados';
      } finally {
        this.loading = false;
      }
    },
    
    formatCNPJ(cnpj) {
      if (!cnpj || cnpj === 'N/A') return cnpj;
      // Remove caracteres não numéricos
      const cleaned = cnpj.replace(/\D/g, '');
      // Aplica a formatação do CNPJ
      return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    },
    
    formatPlano(plano) {
      const planosMap = {
        'PLANO_PLUS_01': 'PLANO PLUS 01',
        'PLANO_BASIC': 'PLANO BASIC',
        'PLANO_ENTERPRISE': 'PLANO ENTERPRISE'
      };
      return planosMap[plano] || plano;
    },
    
    getTipoClass(tipo) {
      const classes = {
        'APP': 'app',
        'WEB': 'web'
      };
      return classes[tipo] || '';
    },
    
    getPlanoClass(plano) {
      const classes = {
        'PLANO_PLUS_01': 'plus',
        'PLANO_BASIC': 'basic',
        'PLANO_ENTERPRISE': 'enterprise'
      };
      return classes[plano] || '';
    },
    
    viewItem(item) {
      alert(`Visualizando: ${item.name}\nEmail: ${item.email}\nCNPJ: ${item.cnpj}`);
    },
    
    editItem(item) {
      alert(`Editando: ${item.name}`);
    },
    
    async toggleStatus(item) {
      try {
        // Aqui você faria a chamada API para atualizar o status
        // Por enquanto, apenas atualizamos localmente
        item.enabled = !item.enabled;
        item.status = item.enabled ? 'Ativo' : 'Inativo';
        
        // Exemplo de chamada API (descomente quando implementar):
        // await apiClient.put(`/cognito/users/${item.username}/status`, {
        //   enabled: item.enabled
        // });
        
      } catch (error) {
        console.error('Erro ao alterar status:', error);
        alert('Erro ao alterar status do usuário');
      }
    },
    
    async deleteItem(item) {
      if (confirm(`Tem certeza que deseja excluir ${item.name}?`)) {
        try {
          // Aqui você faria a chamada API para excluir
          // Por enquanto, apenas removemos localmente
          const index = this.data.findIndex(d => d.username === item.username);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          
          // Exemplo de chamada API (descomente quando implementar):
          // await apiClient.delete(`/cognito/users/${item.username}`);
          
        } catch (error) {
          console.error('Erro ao excluir usuário:', error);
          alert('Erro ao excluir usuário');
        }
      }
    },
    
    exportToExcel() {
      // Simulação de exportação para Excel
      const dataStr = JSON.stringify(this.filteredData, null, 2);
      const dataUri = 'data:application/vnd.ms-excel;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = 'empresas.xls';
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
    }
  }
}
</script>

<style scoped>
.loading-container, .error-container {
  text-align: center;
  padding: 2rem;
}

.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  color: #e74c3c;
}

/* Estilos para os novos badges */
.badge.app {
  background-color: #e3f2fd;
  color: #1976d2;
}

.badge.web {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.badge.plus {
  background-color: #e8f5e8;
  color: #2e7d32;
}

.badge.basic {
  background-color: #fff3e0;
  color: #ef6c00;
}

.badge.enterprise {
  background-color: #fce4ec;
  color: #c2185b;
}
.page-container {
  padding: 2rem;
  background: #f8fafc;
  min-height: calc(100vh - 120px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.page-title {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-2px);
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
}

.btn-secondary:hover {
  background: #cbd5e0;
  transform: translateY(-2px);
}

.filters-section {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.filter-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #4a5568;
  font-size: 0.875rem;
}

.filter-input, .filter-select {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  transition: all 0.3s ease;
}

.filter-input:focus, .filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.datagrid-container {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.table-responsive {
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Poppins', sans-serif;
}

.modern-table th {
  background: #f8fafc;
  padding: 0.35rem;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.modern-table td {
  padding: 0.45rem;
  border-bottom: 1px solid #e2e8f0;
  color: #2d3748;
}

.modern-table tr:hover {
  background: #f7fafc;
}

.modern-table tr.inactive {
  opacity: 0.6;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge.mei { background: #dbeafe; color: #1e40af; }
.badge.ltda { background: #f3e8ff; color: #7c3aed; }
.badge.sa { background: #f0fdf4; color: #166534; }
.badge.basic { background: #fef3c7; color: #92400e; }
.badge.pro { background: #fce7f3; color: #be185d; }
.badge.enterprise { background: #ecfdf5; color: #047857; }

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge.active {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  padding: 0.1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  font-size: 1rem;
}

.btn-action:hover {
  transform: scale(1.1);
}

.btn-action.view:hover { background: #dbeafe; }
.btn-action.edit:hover { background: #fef3c7; }
.btn-action.block:hover { background: #fee2e2; }
.btn-action.unblock:hover { background: #d1fae5; }
.btn-action.delete:hover { background: #fee2e2; }

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-family: 'Poppins', sans-serif;
  color: #4a5568;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
}

.pagination-btn:hover:not(:disabled) {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-page {
  font-family: 'Poppins', sans-serif;
  color: #4a5568;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .filter-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .filter-row {
    grid-template-columns: 1fr;
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>